import React from 'react';
import { Button, Input, Loader } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { History } from 'history';
import { isArray, isNil, uniq } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';
import {
  ConditionSetType,
  ConditionType,
  ContactFormType,
  RuleType,
  SegmentType,
  UserType,
} from '../../../../../shared/types';
import { FilterOperations } from '../../../../../shared/constants';
import {
  Center,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
  FormSection,
} from '../../../../../shared/styles';
import { useGetSegments } from '../../hooks';
import { getModifiedSegmentData } from '../../utils';
import { matchTypes } from './constants';
import SegmentRule from './SegmentRule';

type NewSegmentFormProps = {
  onSave: (formValues: SegmentType) => void;
  disabled?: boolean;
  isSavingSegment?: boolean;
  isExecutingSegment?: boolean;
  segmentData?: SegmentType;
  cloneValue?: SegmentType;
  executeSegment: (rule: RuleType) => void;
  contactForm: ContactFormType;
  userInfo?: UserType;
};

const DEFAULT_CONDITION_SET: ConditionSetType = {
  id: uuid(),
  match_type: matchTypes[0].id,
  conditions: [
    {
      id: uuid(),
      name: '',
      evaluate_on: 'CONTACT',
      operator: '',
      value: '',
      field_type: '',
    },
  ],
};

const NewSegmentForm = (props: NewSegmentFormProps) => {
  const { t } = useTranslation();
  const {
    contactForm,
    cloneValue,
    disabled,
    executeSegment,
    segmentData,
    isSavingSegment = false,
    isExecutingSegment = false,
    onSave,
    userInfo,
  } = props;
  const { push }: History = useHistory();
  const [takenNames, setTakenNames] = React.useState<string[]>([]);

  const { data: segmentsData = {}, isFetching: isLoadingSegments } =
    useGetSegments();

  const form = useForm<SegmentType>({
    resolver: (formValues: SegmentType, ...resolverProps) => {
      return yupResolver(
        Yup.object({
          id: Yup.string(),
          version: Yup.string(),
          name: Yup.string()
            .required(`${t('forms.err_msg.segment_required')}`)
            .trim()
            .max(
              50,
              `${t('forms.err_msg.max_char', {
                len: '50',
              })}`,
            )
            .min(
              3,
              `${t('forms.err_msg.min_char', {
                len: '3',
              })}`,
            )
            .test({
              message: 'Error',
              name: 'name',
              test: function (name, context) {
                const { createError } = context || {};
                const currentTakenNames = !segmentData?.id
                  ? takenNames
                  : takenNames.filter((n) => n !== segmentData.name);
                if (currentTakenNames.includes(name || '')) {
                  return createError({
                    message: `${t('forms.err_msg.segment_exists', {
                      name: name,
                    })}`,
                  });
                }
                return true;
              },
            }),
          rule: Yup.object({
            match_type: Yup.string()
              .oneOf(['ALL', 'ANY'])
              .required(`${t('forms.err_msg.match_type_required')}`),
            condition_sets: Yup.array()
              .of(
                Yup.object({
                  match_type: Yup.string().required(
                    `${t('forms.err_msg.match_type_required')}`,
                  ),
                  conditions: Yup.array()
                    .of(
                      Yup.object({
                        name: Yup.string().required(
                          `${t('forms.err_msg.field_name_required')}`,
                        ),
                        evaluate_on: Yup.string().required(
                          `${t('forms.err_msg.evaluation_required')}`,
                        ),
                        operator: Yup.string().required(
                          `${t('forms.err_msg.operator_required')}`,
                        ),
                        field_type: Yup.string().required(
                          `${t('forms.err_msg.field_type_required')}`,
                        ),
                        value: Yup.mixed().test({
                          test: function (
                            value: any,
                            context: {
                              path: any;
                              parent: ConditionType;
                              createError: (
                                params?: Yup.CreateErrorOptions,
                              ) => Yup.ValidationError;
                            },
                          ) {
                            const {
                              createError,
                              parent: condition,
                              path,
                            } = context || {};
                            if (
                              condition?.operator &&
                              FilterOperations[condition.operator]?.between &&
                              FilterOperations[condition.operator]?.needValue
                            ) {
                              if (!condition.value)
                                return createError({
                                  message: `${t(
                                    'forms.err_msg.required_field',
                                  )}`,
                                });
                              if (condition.value && !condition?.value?.from)
                                return createError({
                                  path: `${path}.from`,
                                  message: `${t(
                                    'forms.err_msg.from_required',
                                  )}`,
                                });
                              if (condition.value && !condition?.value?.to)
                                return createError({
                                  path: `${path}.to`,
                                  message: `${t(
                                    'forms.err_msg.rto_requiredequired',
                                  )}`,
                                });
                            }
                            if (
                              condition?.operator &&
                              FilterOperations[condition.operator]?.needValue &&
                              (!condition.value ||
                                (typeof condition.value === 'string' &&
                                  condition.value.trim() === '') ||
                                (!isNil(condition.value) &&
                                  isArray(condition.value) &&
                                  condition.value.length === 0))
                            ) {
                              return createError({
                                message: `${t('forms.err_msg.required_field')}`,
                              });
                            }
                            return true;
                          },
                        }),
                      }),
                    )
                    .min(
                      1,
                      `${t('forms.err_msg.min_condition_per_group_req', {
                        char: '1',
                      })}`,
                    )
                    .required(`${t('forms.err_msg.conditions_required')}`),
                }),
              )
              .min(
                1,
                `${t('forms.err_msg.min_condition_req', {
                  char: '1',
                })}`,
              )
              .required(`${t('forms.err_msg.condition_sets_required')}`),
          }),
        }).required(),
      )(formValues, ...resolverProps);
    },
    defaultValues: {
      name: '',
      rule: {
        id: uuid(),
        match_type: matchTypes[0].id,
        condition_sets: [DEFAULT_CONDITION_SET],
      },
    },
  });

  const {
    register,
    watch,
    reset,
    handleSubmit,
    formState: { isDirty, submitCount, errors },
  } = form;

  const onRefresh = (data: SegmentType) => {
    if (data.rule)
      executeSegment(
        getModifiedSegmentData(data).rule || {
          condition_sets: [],
          match_type: 'ANY',
        },
      );
  };

  React.useEffect(() => {
    if (
      segmentData &&
      segmentData.id &&
      Object.keys(segmentData || {}).length > 0 &&
      segmentData.version !== watch('version')
    ) {
      reset(segmentData);
    }
  }, [segmentData]);

  React.useEffect(() => {
    const newNames = (segmentsData?.segment_rules || [])
      .map((segment) => segment.name || '')
      .filter((v) => !!v);
    setTakenNames((old) => uniq([...old, ...newNames]));
  }, [segmentsData]);

  React.useEffect(() => {
    if (Object.keys(cloneValue || {}).length > 0) {
      const clonedConditionSets = cloneValue?.rule?.condition_sets || [];
      const newValue: SegmentType = {
        ...cloneValue,
        rule: {
          ...(cloneValue?.rule || { match_type: 'ANY' }),
          condition_sets: [
            ...(isArray(clonedConditionSets) && clonedConditionSets.length > 0
              ? clonedConditionSets
              : [DEFAULT_CONDITION_SET]),
          ],
          match_type: cloneValue?.rule?.match_type || 'ALL',
        },
      };
      reset(getModifiedSegmentData(newValue), {
        keepValues: false,
        keepErrors: false,
        keepTouched: false,
      });
    }
  }, [cloneValue]);

  if (isLoadingSegments)
    return (
      <Center>
        <Loader />
      </Center>
    );

  return (
    <FormContainer
      onSubmit={handleSubmit(onRefresh, (err) => console.log('ERR', err))}
    >
      <Prompt
        when={isDirty && Boolean(watch('id'))}
        message={`${t('segments.not_saved')}`}
      />
      <FormHeader hideBorder>
        <div className="heading">
          {`${
            segmentData?.id
              ? disabled
                ? `${t('segments.view_segement')}`
                : `${t('segments.edit_segement')}`
              : `${t('segments.new_segement')}`
          }`}
        </div>
      </FormHeader>
      <FormContent>
        <FormSection>
          <FormField>
            <Input
              {...register('name')}
              id="name"
              label={`${t('forms.labels.segment_name')}`}
              disabled={disabled}
              placeholder={`${t('forms.placeholders.segment_name')}`}
              isRequired
              isErrored={Boolean(submitCount > 0 && errors.name?.message)}
              errorText={
                submitCount > 0 && errors.name?.message
                  ? errors.name.message
                  : ''
              }
            />
          </FormField>
        </FormSection>
        <FormSection>
          <FormField>
            <SegmentRule
              disabled={disabled}
              contactForm={contactForm}
              form={form}
              userInfo={userInfo}
            />
          </FormField>
        </FormSection>
      </FormContent>
      <FormActions>
        <div>
          <Button
            inline
            htmlType="button"
            type="secondary"
            onClick={() => push('/segments')}
          >
            {t('cancel')}
          </Button>
        </div>
        <div>
          <Button
            inline
            type="secondary"
            htmlType="submit"
            disabled={isExecutingSegment || isSavingSegment}
          >
            {isExecutingSegment
              ? `${t('contacts.fetching')}`
              : `${t('contacts.refresh_contacts')}`}
          </Button>
          <Button
            inline
            htmlType="button"
            onClick={() => {
              handleSubmit(onSave, (err) => console.log('ERR', err))();
            }}
            disabled={
              (segmentData?.id && !isDirty) ||
              isSavingSegment ||
              isExecutingSegment ||
              disabled
            }
          >
            {t('save')}
          </Button>
        </div>
      </FormActions>
    </FormContainer>
  );
};

export default NewSegmentForm;
